import { useEffect, useState } from 'react'
import { billingService } from '../services/billing'
import { adminService, type Wallet, type WalletTransaction } from '../../identity/services/admin'
import type { Subscription, ActiveFeatures } from '../../../types'
import { LOW_WALLET_THRESHOLD } from '../../../lib/constants'
import { useWindowWidth } from '../../../lib/useWindowWidth'
import {
  CreditCard, CheckCircle, RefreshCw, Wallet as WalletIcon,
  ArrowUpRight, ArrowDownRight, ChevronDown, ChevronUp, Clock,
} from 'lucide-react'

const SUBSCRIPTION_STATUS_BADGE: Record<string, string> = {
  active:    'badge-success',
  suspended: 'badge-warning',
  cancelled: 'badge-danger',
  expired:   'badge-neutral',
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n)

const fmtDateTime = (s: string) =>
  new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })

export default function MySubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [features, setFeatures]           = useState<ActiveFeatures | null>(null)
  const [wallet, setWallet]               = useState<Wallet | null>(null)
  const [txns, setTxns]                   = useState<WalletTransaction[]>([])
  const [showTxns, setShowTxns]           = useState(false)
  const [loading, setLoading]             = useState(true)
  const [loadingTxns, setLoadingTxns]     = useState(false)
  const [expandedSubId, setExpandedSubId] = useState<number | null>(null)
  const isMobile = useWindowWidth() < 768

  const load = async () => {
    setLoading(true)
    try {
      const [subs, feats, w] = await Promise.all([
        billingService.getMySubscriptions(),
        billingService.getMyFeatures(),
        adminService.getMyWallet().catch(() => null),   // graceful — may not be provisioned yet
      ])
      setSubscriptions(subs)
      setFeatures(feats)
      setWallet(w)
    } finally {
      setLoading(false)
    }
  }

  const loadTxns = async () => {
    if (txns.length > 0) { setShowTxns(v => !v); return }
    setLoadingTxns(true)
    try {
      const data = await adminService.getMyTransactions()
      setTxns(data)
      setShowTxns(true)
    } finally {
      setLoadingTxns(false)
    }
  }

  useEffect(() => { load() }, [])

  const balance = wallet ? Number(wallet.balance) : 0
  const isLow   = wallet && balance < LOW_WALLET_THRESHOLD

  // Monthly burn rate from active subscriptions (annual normalised to monthly)
  const monthlyBurn = subscriptions
    .filter(s => s.status?.toLowerCase() === 'active')
    .reduce((sum, s) => {
      const price = Number(s.price_per_cycle ?? 0)
      return sum + (s.billing_cycle?.toLowerCase() === 'annual' ? price / 12 : price)
    }, 0)

  const runwayDays = wallet && monthlyBurn > 0
    ? Math.floor((balance / monthlyBurn) * 30)
    : null

  const runwayColor =
    runwayDays === null  ? 'var(--text-tertiary)' :
    runwayDays >= 60     ? 'var(--success-text)'  :
    runwayDays >= 30     ? 'var(--warning-text)'  :
                           'var(--danger-text)'

  return (
    <div className="space-y-6">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <CreditCard className="h-6 w-6" style={{ color: 'var(--brand-600)' }} />
            My Plan
            <button onClick={load} disabled={loading} className="btn-icon" title="Refresh" aria-label="Refresh" style={{ marginLeft: 'auto' }}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </h1>
          <p className="page-subtitle">
            {loading ? 'Loading…' : `${subscriptions.length} subscription${subscriptions.length !== 1 ? 's' : ''}`}
          </p>
        </div>
      </div>

      {/* ── Wallet balance card ─────────────────────────────────────────── */}
      {wallet && (
        <div className="card space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className="rounded-lg p-2.5 shrink-0"
                style={{
                  background: isLow ? 'var(--danger-bg)' : 'var(--success-bg)',
                }}
              >
                <WalletIcon
                  className="h-5 w-5"
                  style={{ color: isLow ? 'var(--danger-text)' : 'var(--success-text)' }}
                />
              </div>
              <div>
                <p className="stat-card-label">Wallet Balance</p>
                <p
                  className="text-2xl font-bold tabular-nums"
                  style={{ color: isLow ? 'var(--danger-text)' : 'var(--text-primary)' }}
                >
                  {fmt(balance)}
                </p>
                {runwayDays !== null && (
                  <p className="text-xs mt-1 flex items-center gap-1" style={{ color: runwayColor }}>
                    <Clock className="h-3 w-3 shrink-0" />
                    ~{runwayDays} day{runwayDays !== 1 ? 's' : ''} of runway
                    {monthlyBurn > 0 && (
                      <span style={{ color: 'var(--text-tertiary)' }}>
                        · ₹{Math.round(monthlyBurn).toLocaleString('en-IN')}/mo burn
                      </span>
                    )}
                  </p>
                )}
                {isLow && (
                  <p className="text-xs mt-0.5" style={{ color: 'var(--danger-text)' }}>
                    Low balance — contact your Operon account manager to top up
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={loadTxns}
              disabled={loadingTxns}
              className="btn-secondary shrink-0 flex items-center gap-1.5"
              style={{ height: 32, fontSize: 12 }}
            >
              {loadingTxns ? (
                <div className="spinner" style={{ width: 14, height: 14 }} />
              ) : showTxns ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
              {showTxns ? 'Hide' : 'Transactions'}
            </button>
          </div>

          {/* Transaction history (expandable) */}
          {showTxns && txns.length > 0 && (
            <div
              className="rounded-lg overflow-hidden"
              style={{ border: '1px solid var(--border-default)' }}
            >
              <div
                className="px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                style={{
                  background: 'var(--surface-subtle)',
                  color: 'var(--text-tertiary)',
                  borderBottom: '1px solid var(--border-default)',
                }}
              >
                Recent transactions
              </div>
              <div className="divide-y" style={{ borderColor: 'var(--border-default)' }}>
                {txns.slice(0, 20).map(t => (
                  <div key={t.id} className="flex items-start gap-3 px-4 py-3">
                    <div
                      className="mt-0.5 rounded-full p-1.5 shrink-0"
                      style={{
                        background: t.transaction_type === 'DEBIT' ? 'var(--danger-bg)' : 'var(--success-bg)',
                      }}
                    >
                      {t.transaction_type === 'DEBIT' ? (
                        <ArrowDownRight className="h-3 w-3" style={{ color: 'var(--danger-text)' }} />
                      ) : (
                        <ArrowUpRight className="h-3 w-3" style={{ color: 'var(--success-text)' }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" title={t.description} style={{ color: 'var(--text-primary)' }}>
                        {t.description}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                        {fmtDateTime(t.created_at)}
                        {t.reference && (
                          <span className="ml-2 font-mono">{t.reference}</span>
                        )}
                      </p>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <p
                        className="text-sm font-semibold tabular-nums"
                        style={{ color: t.transaction_type === 'DEBIT' ? 'var(--danger-text)' : 'var(--success-text)' }}
                      >
                        {t.transaction_type === 'DEBIT' ? '−' : '+'}
                        {fmt(t.amount)}
                      </p>
                      <p className="text-xs tabular-nums mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                        bal {fmt(t.balance_after)}
                      </p>
                    </div>
                  </div>
                ))}
                {txns.length === 0 && (
                  <p className="text-sm text-center py-6" style={{ color: 'var(--text-tertiary)' }}>
                    No transactions yet
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Active features ─────────────────────────────────────────────── */}
      {features && (
        <div className="card">
          <h2 className="font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <CheckCircle className="h-5 w-5" style={{ color: 'var(--success-text)' }} />
            Active Features
          </h2>
          {features.active_slugs.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No active features</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {features.has_core && <span className="badge-info">core</span>}
              {features.active_slugs.filter(s => s !== 'core').map(slug => (
                <span key={slug} className="badge-success">{slug}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Subscriptions ───────────────────────────────────────────────── */}
      {isMobile ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2 px-1" style={{ color: 'var(--text-tertiary)' }}>
            Subscription Details
          </p>
          {loading ? (
            <div className="flex justify-center py-8"><div className="spinner" /></div>
          ) : subscriptions.length === 0 ? (
            <div className="empty-state"><p style={{ color: 'var(--text-tertiary)' }}>No active subscriptions. Contact your Operon account manager.</p></div>
          ) : (
            <div className="flex flex-col gap-2">
              {subscriptions.map(sub => {
                const expanded = expandedSubId === sub.id
                const cycleLabel = sub.billing_cycle
                  ? sub.billing_cycle.charAt(0).toUpperCase() + sub.billing_cycle.slice(1).toLowerCase()
                  : '—'
                return (
                  <div key={sub.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div className="flex items-center gap-2.5 px-3 py-3 cursor-pointer" onClick={() => setExpandedSubId(expanded ? null : sub.id)}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{sub.service_name}</p>
                        <p className="text-xs font-mono" style={{ color: 'var(--text-tertiary)' }}>{sub.pricing_tier} · {cycleLabel}</p>
                      </div>
                      <span className={`shrink-0 ${SUBSCRIPTION_STATUS_BADGE[sub.status] || 'badge-neutral'}`} style={{ fontSize: 10 }}>
                        {sub.status}
                      </span>
                      <ChevronDown className="h-4 w-4 shrink-0 transition-transform" style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }} />
                    </div>
                    {expanded && (
                      <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                        <div className="flex justify-between text-xs py-1">
                          <span style={{ color: 'var(--text-tertiary)' }}>Price</span>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                            {sub.price_per_cycle != null ? fmt(Number(sub.price_per_cycle)) : '—'}/{sub.billing_cycle === 'annual' ? 'yr' : 'mo'}
                          </span>
                        </div>
                        {sub.next_billing_at && (
                          <div className="flex justify-between text-xs py-1">
                            <span style={{ color: 'var(--text-tertiary)' }}>Next billing</span>
                            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{new Date(sub.next_billing_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                          </div>
                        )}
                        {sub.expires_at && (
                          <div className="flex justify-between text-xs py-1">
                            <span style={{ color: 'var(--text-tertiary)' }}>Expires</span>
                            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{new Date(sub.expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="table-card">
          <div className="table-card-header">
            <h2 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Subscription Details</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Plan / Cycle</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Next Billing</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-8"><div className="spinner mx-auto" /></td></tr>
                ) : subscriptions.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8" style={{ color: 'var(--text-tertiary)' }}>No active subscriptions. Contact your Operon account manager.</td></tr>
                ) : (
                  subscriptions.map(sub => (
                    <tr key={sub.id}>
                      <td>
                        <p className="text-primary-data">{sub.service_name}</p>
                        <p className="text-xs font-mono mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{sub.service_slug}</p>
                      </td>
                      <td>{sub.pricing_tier || '—'} / {sub.billing_cycle || '—'}</td>
                      <td>{sub.price_per_cycle != null ? fmt(Number(sub.price_per_cycle)) : '—'}</td>
                      <td><span className={SUBSCRIPTION_STATUS_BADGE[sub.status] || 'badge-neutral'}>{sub.status}</span></td>
                      <td style={{ color: 'var(--text-tertiary)' }}>{sub.next_billing_at ? new Date(sub.next_billing_at).toLocaleDateString() : '—'}</td>
                      <td style={{ color: 'var(--text-tertiary)' }}>{sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
